import React from 'react';
import { StandardPdfTemplate } from '../../templates/StandardPdfTemplate';
import { ProjectData, ProjectInfoData } from '../../shared/types/PdfCommonTypes';
import { TFunction } from 'i18next';
import { IGeogridData } from '../../../../api/IApiResponse';
import { UnpavedResultsCore } from './sections/UnpavedResultsCore';
import { UnpavedGeoGridCore } from './sections/UnpavedGeoGridCore';

interface UnpavedCombinedResultsPdfDocumentProps {
    projectInfo: any;
    dynamicProject: any;
    output: any;
    tireImageUnreinforced?: string;
    tireImageReinforced?: string;
    tireImageGeotextile?: string;
    chartImage?: string;
    geoGridData?: IGeogridData;
    geogridImage?: string;
    t: TFunction;
}

const UnpavedCombinedResultsPdfDocumentV2: React.FC<UnpavedCombinedResultsPdfDocumentProps> = ({
    projectInfo,
    dynamicProject,
    output,
    tireImageUnreinforced,
    tireImageReinforced,
    tireImageGeotextile,
    chartImage,
    geoGridData,
    geogridImage,
    t,
}) => {
    const projectData = dynamicProject?.data;
    const projectInfoData = projectData?.projectInfo;
    const projectType = 'unpaved';
    const unitSystem = projectData?.unit || 'Metric';
    const calculationMethodKey = projectData?.method || projectInfo?.method || projectInfoData?.data?.method;
    const projectInfoDescriptionKey = calculationMethodKey === 'leng' ? 'unpaved_leng_project_info_desc_pdf' : 'unpaved_giroud_project_info_desc_pdf';
    const projectInfoDescription = t(projectInfoDescriptionKey) || 'Project Information';

    // Prepare data for StandardPdfTemplate
    const projectDataForTemplate: ProjectData = {
        projectName: projectData?.projectName || 'N/A',
        projectType: projectType,
        unit: unitSystem,
        mapImage: projectData?.mapImage,
    };

    const projectInfoDataForTemplate: ProjectInfoData = {
        data: {
            nrProject: projectInfo?.nrProject || projectData?.projectInfo?.data?.nrProject || 'N/A',
        },
        projectOwner: projectInfo?.projectOwner || projectData?.projectInfo?.projectOwner || 'N/A',
        designerFirmName: projectInfo?.designerFirmName || projectData?.projectInfo?.designerFirmName || 'N/A',
        designerFirmCountry: projectInfo?.designerFirmCountry || projectData?.projectInfo?.designerFirmCountry || 'N/A',
        designerFirmCity: projectInfo?.designerFirmCity || projectData?.projectInfo?.data?.designerFirmCity || 'N/A',
        coordinates: projectInfo?.coordinates || projectData?.projectInfo?.coordinates || [null, null],
    };

    return (
        <StandardPdfTemplate
            projectType={projectType}
            projectData={projectDataForTemplate}
            projectInfoData={projectInfoDataForTemplate}
            t={t}
            headerConfig={{
                customTitle: t('pdf_header_technical_report_groundmac') || 'GROUNDMAC - Design Technical Report',
                customProjectType: t('project_type_road_unpaved') || 'Roads Unpaved',
                showGeogridSubtitle: true,
            }}
            footerConfig={{
                customText: t('pdf_footer') || 'MACread eDesign',
                customNotice: t('pdf_footer_notice') || 'Footer notice',
            }}
            showProjectInfo={true}>
            {/* Use the shared core component for Unpaved Results */}
            <UnpavedResultsCore
                output={output}
                projectData={projectData}
                projectInfo={projectInfo}
                tireImageUnreinforced={tireImageUnreinforced}
                tireImageReinforced={tireImageReinforced}
                tireImageGeotextile={tireImageGeotextile}
                chartImage={chartImage}
                t={t}
                projectInfoDescription={projectInfoDescription}
                showInputData={true}
                showChart={true}
                showResults={true}
            />

            {/* Use the shared core component for GeoGrid Results */}
            <UnpavedGeoGridCore
                geoGridData={geoGridData}
                geogridImage={geogridImage}
                projectData={projectData}
                t={t}
                showInputData={true}
                showGeoGridImage={true}
                showCourseResults={true}
                customTitle="GeoGrid Reinforcement Details"
            />
        </StandardPdfTemplate>
    );
};

export default UnpavedCombinedResultsPdfDocumentV2;
